import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection";
import { FaArrowRight, FaCalendar, FaMapMarkerAlt } from "react-icons/fa";
import groupEventAsset from "../../assets/gallery/events/groupix.png";
import techTraining1 from "../../assets/gallery/education/tech-training1.jpg";
import techTraining2 from "../../assets/gallery/education/tech-training2.jpg";
import techTraining3 from "../../assets/gallery/education/tech-training3.jpg";
import techTraining4 from "../../assets/gallery/education/tech-training4.jpg";
import techTraining5 from "../../assets/gallery/education/tech-training5.png";
import healthOutreach1 from "../../assets/gallery/health/health-outreach1.jpeg";
import classStudents from "../../assets/gallery/education/classstudents.png";
import scholarship from "../../assets/gallery/education/scholarship.jpeg";
import health from "../../assets/gallery/health/health.jpeg";
import { eventAPI, resolveMediaUrl } from "../../services/api";
import { getCollageImages } from "../../utils/heroImages";

const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  if (isInView) {
    controls.start("visible");
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function NewsAndEvents() {
  const groupEvent = `${groupEventAsset}?v=20260424`;
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const response = await eventAPI.getPublic(50, { upcomingOnly: true });
        setEvents(response.events || []);
      } catch (error) {
        console.error('Failed to load events page data:', error);
      }
    };
    loadEvents();
  }, []);

  const news = [
    {
      title: "Ojoto Union Reaches 500 Member Milestone",
      date: "April 5, 2024",
      description: "We're excited to announce that Ojoto Union North America has reached 500 active members across the United States and Canada."
    },
    {
      title: "New Scholarship Program Launched",
      date: "March 15, 2024",
      description: "We've launched a new scholarship program specifically for students pursuing STEM fields."
    },
    {
      title: "Community Development Project Completed",
      date: "February 28, 2024",
      description: "Our latest community development project in Ojoto has been successfully completed, improving infrastructure for local residents."
    }
  ];

  return (
    <div className="w-full bg-white">
      <HeroSection
        title="News & Events"
        subtitle="Stay updated with the latest news, events, and happenings in the Ojoto Union community."
        buttons={[
          { text: "Apply Now", link: "/register", variant: "blue" },
          { text: "Login", link: "/login", variant: "blue" }
        ]}
        collageImages={getCollageImages("newsEvents")}
      />

      {/* Upcoming Events */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Upcoming Events
              </h2>
            </div>
          </AnimatedSection>

          {events.length === 0 ? (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white border border-gray-200 rounded-xl p-10 text-center shadow-sm">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-50 text-blue-700 mb-4">
                  <FaCalendar className="text-xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No Upcoming Events</h3>
                <p className="text-gray-600 leading-relaxed">
                  There are currently no upcoming events scheduled. Please check back soon for new announcements.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {events.map((event, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1} className="h-full">
                <div className="card h-full flex flex-col">
                  {event.media_type === 'video' && event.media_url ? (
                    <video
                      src={resolveMediaUrl(event.media_url)}
                      controls
                      className="w-full h-48 rounded-md mb-4 card-image bg-gray-100"
                      style={{
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                        objectFit: 'cover',
                        objectPosition: 'center'
                      }}
                    />
                  ) : (
                    <img
                      src={resolveMediaUrl(event.media_url || event.banner_url) || groupEvent}
                      alt={event.title}
                      className="w-full h-48 object-contain rounded-md mb-4 card-image bg-gray-100"
                      style={{
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                        objectFit: 'cover',
                        objectPosition: 'center'
                      }}
                    />
                  )}
                  <h3
                    className="text-xl font-bold text-gray-800 mb-3"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: '3.5rem'
                    }}
                  >
                    {event.title}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-2">
                      <FaCalendar />
                      <span>{new Date(event.event_date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaMapMarkerAlt />
                      <span>{event.event_location || 'Ojoto Union NA'}</span>
                    </div>
                  </div>
                  <p
                    className="text-gray-600 mb-4"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: '6rem'
                    }}
                  >
                    {event.description}
                  </p>
                  <div className="flex flex-wrap gap-3 mt-auto">
                    <Link
                      to={typeof event.id === 'number' ? `/resources/news-events/${event.id}` : '/resources/news-events'}
                      className="text-primary-600 font-medium hover:text-primary-700 inline-flex items-center gap-2"
                    >
                      Read Event Details
                      <FaArrowRight className="text-sm" />
                    </Link>
                    {event.video_url && (
                      <a
                        href={event.video_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-700 font-medium hover:text-blue-800 inline-flex items-center gap-2"
                      >
                        Event Video
                      </a>
                    )}
                  </div>
                </div>
              </AnimatedSection>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Latest News */}
      <section className="section bg-gray-50">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Latest News
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-6">
            {news.map((item, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div className="card">
                  <div className="text-sm text-gray-500 mb-2">
                    {item.date}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">
                    {item.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
